import { Controller, UseFormReturn } from "react-hook-form";
import { InputDate } from "../../Atoms/Inputs/InputDate";
import { IGerenciamentoProps } from "../../../types/gerenciamento";
import { ISelectOptions } from "../../../types/inputs";
import dayjs from "dayjs";

export const ISO = "YYYY-MM-DD";
const PRESETS = [
  { label: "Hoje", start: () => dayjs() },
  { label: "7 dias", start: () => dayjs().subtract(6, "day") },
  { label: "Mês atual", start: () => dayjs().startOf("month") },
  { label: "30 dias", start: () => dayjs().subtract(29, "day") },
];

export const card = "rounded-2xl border border-[#E7E6E2] bg-white p-5";
const fieldLabel =
  "text-xs font-semibold uppercase tracking-wider text-[#6B6B66]";
export const num = (v?: number) => (v || 0).toLocaleString("pt-BR");
export const pct = (a?: number, b?: number) => (b ? ((a || 0) / b) * 100 : 0);
export const pctLabel = (v: number) => `${v.toFixed(1).replace(".", ",")}%`;

interface IPart {
  label: string;
  value?: number;
  color: string;
}

export const Breakdown = ({
  title,
  parts,
  format = num,
  children,
}: {
  title: string;
  parts: IPart[];
  format?: (v?: number) => string;
  children?: React.ReactNode;
}) => {
  const total = parts.reduce((acc, p) => acc + (p.value || 0), 0);
  return (
    <div className={`${card} flex flex-col gap-4`}>
      <span className="text-sm font-semibold">{title}</span>
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[#F1F0EC]">
        {parts.map((p) => (
          <span
            key={p.label}
            style={{ width: `${pct(p.value, total)}%`, background: p.color }}
          />
        ))}
      </div>
      <div className="flex flex-col gap-2.5">
        {parts.map((p) => (
          <div key={p.label} className="flex items-center gap-2.5 text-sm">
            <span
              className="h-2.5 w-2.5 rounded-[3px]"
              style={{ background: p.color }}
            />
            <span className="flex-1 text-[#4A4A46]">{p.label}</span>
            <span className="font-semibold tabular-nums">
              {format(p.value)}
            </span>
            <span className="w-14 text-right tabular-nums text-[#6B6B66]">
              {pctLabel(pct(p.value, total))}
            </span>
          </div>
        ))}
      </div>
      {children}
    </div>
  );
};

type FilterForm = UseFormReturn<IGerenciamentoProps>;

export const ReportFilters = ({
  control,
  setValue,
  watch,
  onSubmit,
  options,
  colors,
}: {
  control: FilterForm["control"];
  setValue: FilterForm["setValue"];
  watch: FilterForm["watch"];
  onSubmit: () => void;
  options: ISelectOptions[];
  colors: Record<string, string>;
}) => {
  const today = dayjs().format(ISO);
  const [dataInicio, dataFim] = watch(["dataInicio", "dataFim"]);

  return (
    <form
      onSubmit={onSubmit}
      className={`${card} flex flex-wrap items-end gap-5 py-4`}
    >
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>Período</span>
        <div className="flex items-center gap-2">
          <div className="w-[170px]">
            <Controller
              control={control}
              name="dataInicio"
              render={({ field: { onChange, value } }) => (
                <InputDate
                  required
                  showIcon
                  selected={value && dayjs(value).toDate()}
                  placeholderText="___/___/___"
                  onChange={(e) => onChange(dayjs(e).format(ISO))}
                />
              )}
            />
          </div>
          <span className="text-[#6B6B66]">–</span>
          <div className="w-[170px]">
            <Controller
              control={control}
              name="dataFim"
              render={({ field: { onChange, value } }) => (
                <InputDate
                  required
                  showIcon
                  maxDate={new Date()}
                  selected={value && dayjs(value).toDate()}
                  placeholderText="___/___/___"
                  onChange={(e) => onChange(dayjs(e).format(ISO))}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>Atalhos</span>
        <div className="flex h-11 gap-0.5 rounded-[10px] bg-[#F1F0EC] p-1">
          {PRESETS.map((p) => {
            const start = p.start().format(ISO);
            const active = dataInicio === start && dataFim === today;
            return (
              <button
                key={p.label}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setValue("dataInicio", start);
                  setValue("dataFim", today);
                }}
                className={`rounded-md px-3 text-[13px] ${
                  active
                    ? "bg-white font-semibold shadow-sm"
                    : "font-medium text-[#4A4A46]"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-[1_1_360px] flex-col gap-1.5">
        <span className={fieldLabel}>Empresa</span>
        <Controller
          control={control}
          name="empresa"
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-wrap gap-2">
              {[{ label: "Todas", value: "" }, ...options].map((opt) => {
                const active = (value || "") === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange(opt.value)}
                    className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-[13px] ${
                      active
                        ? "border-[#1D1D1B] bg-[#1D1D1B] font-semibold text-white"
                        : "border-[#DAD9D4] bg-white font-medium"
                    }`}
                  >
                    {opt.value && (
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: colors[opt.value] }}
                      />
                    )}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        />
      </div>

      <button
        type="submit"
        className="h-11 rounded-[10px] bg-[#E84E1B] px-6 text-sm font-semibold text-white hover:bg-[#cf4316]"
      >
        Aplicar filtros
      </button>
    </form>
  );
};

export const ChartLegend = ({
  series,
}: {
  series: { label: string; color: string }[];
}) => (
  <div className="flex flex-wrap gap-3.5">
    {series.map((s) => (
      <span
        key={s.label}
        className="inline-flex items-center gap-1.5 text-[13px] text-[#4A4A46]"
      >
        <span
          className="h-[3px] w-3.5 rounded-sm"
          style={{ background: s.color }}
        />
        {s.label}
      </span>
    ))}
  </div>
);

export const chartSx = {
  "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
    stroke: "#DAD9D4 !important",
  },
  "& .MuiChartsAxis-tickLabel": { fill: "#6B6B66 !important" },
};
